'use client'

import { InformationDialog, PaymentType } from '@repo/shared/components'
import {
  useConvertNumber,
  useErrorHandler,
  useFeatureFlag,
  useLanguage,
  useTranslate,
} from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDebounce } from '@repo/ui/hooks'
import { useDepositOnlinePaymentSchema } from '@repo/shared/schemas'
import {
  useCalculateDepositCost,
  useGetBanks,
  usePaymentInitiateDepositFiat,
  UserUserBankAccount,
} from '@repo/services'
import { convertToCardNumber } from '@repo/tools'
import {
  Button,
  Card,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  InputNumber,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@repo/ui/components'
import { bankDetailsList } from '@repo/ui/components/bank-card-field/constants'
import { useIsDesktop } from '@repo/ui/hooks'
import { DepositOnlinePaymentAddCard } from './deposit-online-payment.add-card'
import { DepositOnlinePaymentCTA } from './deposit-online-payment.cta'
import { useEffect, useMemo } from 'react'
import { ActionDetailsCondition } from '@repo/ui/components'
import { Alert } from '@repo/ui/components/alert'

type Card = {
  enName: string | undefined
  card: string | undefined
  sheba: string | undefined
  default: boolean
  id: number
}

export const DepositOnlinePaymentContainer = () => {
  const { t } = useTranslate()
  const { convertNumberToLetter } = useConvertNumber()
  const { data, isLoading } = useGetBanks({})
  const { errorHandler } = useErrorHandler()

  const { isFlagEnabled: isMustOwnsCardFlagEnabled } = useFeatureFlag(
    ['fiat-deposit', 'value', 'must_owns_card'],
    { returnBoolean: true },
  )

  const language = useLanguage()
  const cards: Card[] = useMemo(
    () =>
      (data?.data?.bankAccounts || [])
        .filter((bank) => bank.type === 'card')
        .map((bank: UserUserBankAccount) => ({
          id: bank.id ?? 0,
          enName: bank.details?.bankName ?? 'Unknown',
          card:
            bank.type === 'card'
              ? convertToCardNumber(Number(bank.data))
              : undefined,
          sheba: bank.details?.iban,
          default: bank.default ?? false,
        })),
    [data?.data?.bankAccounts],
  )
  const defaultCard: Card | undefined = useMemo(
    () => cards.find((c) => c.default),
    [cards],
  )

  const { schema } = useDepositOnlinePaymentSchema(
    isMustOwnsCardFlagEnabled ? true : cards.length > 0,
  )
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      card: defaultCard ? (defaultCard.card ?? '') : undefined,
      amount: undefined,
    },
  })
  useEffect(() => {
    form.setValue('card', defaultCard ? (defaultCard.card ?? '') : undefined)
  }, [defaultCard, form])

  const amount = +form.watch('amount')?.replace(/,/g, '') * 10
  const debouncedAmount = useDebounce(amount, 500)
  const { data: { data: calculateDepositCost } = {} } = useCalculateDepositCost(
    {
      amount: debouncedAmount,
      currency: 'IRR',
    },
  )

  const paymentInitiateDepositFiatMutation = usePaymentInitiateDepositFiat({})
  const onSubmit = (data: FormSchema<typeof schema>) => {
    paymentInitiateDepositFiatMutation.mutate(
      {
        provider: 'vandar',
        amount: amount,
        currency: 'IRR',
        successURL: `${window.location.origin}/${language}/callback?status=success`,
        failureURL: `${window.location.origin}/${language}/callback?status=error`,
        reference: `deposit-${amount}${cards.length > 0 ? '-' + cards.find((c) => c.card === data.card)?.id : ''}`,
        bankID:
          cards.length > 0
            ? cards.find((c) => c.card === data.card)?.id
            : undefined,
      },
      {
        onSuccess: (res) => {
          if (res?.data?.redirectURL) {
            setTimeout(() => {
              window.open(res?.data?.redirectURL, '_self')
            })
          }
        },
        onError: errorHandler,
      },
    )
  }

  const isDesktop = useIsDesktop()

  const getBankIcon = (enName: string) => {
    const logo = bankDetailsList.find(
      (bank) =>
        bank.enNames.some(
          (name) => name.toLowerCase() === enName.toLowerCase(),
        ) || bank.name.toLowerCase() === enName.toLowerCase(),
    )
    return logo?.icon ?? <Icon name='CardLine' size='sm' />
  }

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {isDesktop && <DepositOnlinePaymentCTA origin='container' />}
      {isMustOwnsCardFlagEnabled && cards.length === 0 && !isLoading && (
        <Alert
          color='alert'
          size='md'
          icon={<Icon name='AlertTriangleLine' size='sm' />}
          caption={t('depositOnlinePaymentPage_add_card_hint')}
        />
      )}
      {!isMustOwnsCardFlagEnabled && cards.length === 0 && !isLoading && (
        <Alert
          color='alert'
          size='md'
          icon={<Icon name='AlertTriangleLine' size='sm' />}
          caption={t('depositOnlinePaymentPage_add_card_hint_optional')}
        />
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id='deposit-online-payment-form'
        >
          <Col className='gap-md'>
            <FormField
              control={form.control}
              name='card'
              extra={
                cards.length > 0 && !isLoading ? (
                  <DepositOnlinePaymentAddCard />
                ) : undefined
              }
              render={({ field }) =>
                cards.length > 0 && !isLoading ? (
                  <Select {...field} onValueChange={field.onChange}>
                    <FormItemWrapper
                      title={t('depositOnlinePaymentPage_select_card')}
                      inputSize='md'
                    >
                      <SelectTrigger inputSize='md' className='w-full'>
                        <SelectValue
                          placeholder={t(
                            'depositOnlinePaymentPage_select_card_placeholder',
                          )}
                        />
                      </SelectTrigger>
                    </FormItemWrapper>
                    <SelectContent className='min-h-10'>
                      {cards.map((c, idx) => (
                        <SelectItem
                          key={idx}
                          value={c.card || c.sheba || String(idx)}
                        >
                          <Row className='gap-xs items-center'>
                            {c.enName && getBankIcon(c.enName)}
                            <Typography size='md'>
                              {c.card || c.sheba}
                            </Typography>
                          </Row>
                        </SelectItem>
                      ))}
                      {cards.length === 0 && !isLoading && (
                        <Col className='min-h-20 text-center m-auto gap-md'>
                          <Typography
                            size='md'
                            className='m-auto'
                            color='text-neutral-surface-80'
                          >
                            {t('depositOnlinePaymentPage_select_card_empty')}
                          </Typography>
                        </Col>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <FormItemWrapper
                    title={t('depositOnlinePaymentPage_select_card')}
                    inputSize='md'
                  >
                    <DepositOnlinePaymentAddCard buttonType='full' />
                  </FormItemWrapper>
                )
              }
            />

            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper
                  title={t('depositOnlinePaymentPage_amount_desired_toman')}
                  inputSize='md'
                  caption={t('depositOnlinePaymentPage_amount_in_words', {
                    number: convertNumberToLetter(
                      field.value?.replace(/,/g, '') ?? 0,
                    ),
                  })}
                >
                  <InputNumber
                    {...field}
                    thousandSeparator={true}
                    // onChange={(e) => field.onChange(Number(e.target.value))}
                    placeholder={t('depositOnlinePaymentPage_enter_amount')}
                    inputSize='md'
                    className='w-full'
                  />
                </FormItemWrapper>
              )}
            />

            <ActionDetailsCondition>
              <Row className='justify-between mt-md mb-xs'>
                <Row className='gap-2xs'>
                  <Typography
                    size='xs'
                    weight='regular'
                    color='text-neutral-surface-80'
                  >
                    {t('depositOnlinePaymentPage_shaparak_fee')}
                  </Typography>
                  <InformationDialog
                    title={t('depositOnlinePaymentPage_shaparak_fee')}
                  >
                    {t('info_shaparak_fee')}
                  </InformationDialog>
                </Row>

                <PaymentType
                  size='2xs'
                  numberColor='text-neutral-surface-80'
                  isBold={false}
                  value={calculateDepositCost?.charge}
                />
              </Row>
              <Row className='justify-between mb-lg'>
                <Typography
                  size='xs'
                  weight='regular'
                  color='text-neutral-surface-80'
                >
                  {t('depositOnlinePaymentPage_final_deposit')}
                </Typography>

                <PaymentType
                  size='2xs'
                  numberColor='text-neutral-surface-80'
                  isBold={false}
                  value={
                    amount ? amount - (calculateDepositCost?.charge || 0) : 0
                  }
                />
              </Row>
              <Button
                size='lg'
                color='base'
                className='w-full mt-lg'
                type='submit'
                loading={
                  paymentInitiateDepositFiatMutation.isPending || isLoading
                }
              >
                {t('depositOnlinePaymentPage_pay')}
              </Button>
            </ActionDetailsCondition>
          </Col>
        </form>
      </Form>
    </Card>
  )
}
